import { Transition } from '@headlessui/react'
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { isArray, size } from 'lodash'
import { memo, useState } from 'react'
import { useIntl } from 'react-intl'
import JSONPretty from 'react-json-pretty'

import Button from '@/components/Button'
import messages from '@/pages/SpaceAppBuilder/utilities/messages'

type TruncatedJSONPrettyProps = {
  props?: any
  title?: string
  data: any
}

const jsonPrettyTheme = {
  main: 'line-height:1.3;color:#000000;background:#f7f7f7;overflow:auto;margin-top: 10px;margin-bottom:10px; border-radius:4px;',
  error:
    'line-height:1.3;color:#000000;background:#f7f7f7;overflow:auto;margin-top: 10px;margin-bottom:10px; border-radius:4px;',
  key: 'color:#116cb0;',
  string: 'color:#000000;',
  value: 'color:#000000;',
  boolean: 'color:#1f7615;'
}

function timeout(delay: number) {
  return new Promise(res => setTimeout(res, delay))
}

// The array length to truncate to. When changing, update this value in the test as well
const MAXLENGTH = 20

const Header = ({ title, copied, onClick }: { title: string; copied: boolean; onClick: () => void }) => (
  <div className="flex justify-between pr-3">
    <div>{title}</div>
    <div className="flex">
      <Transition
        show={copied}
        enter="transition-opacity duration-125"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={`mr-1 select-none text-sm font-thin text-gray-400`}>Copied!</div>
      </Transition>
      <ClipboardDocumentIcon
        className="h-5 w-5 stroke-gray-600 hover:cursor-pointer hover:stroke-gray-400"
        onClick={onClick}
      />
    </div>
  </div>
)

const TruncatedJSONPretty = memo(({ data, title = '' }: TruncatedJSONPrettyProps) => {
  const intl = useIntl()

  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    if (!copied) {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2))
      setCopied(true)
      await timeout(4000)
      setCopied(false)
    }
  }
  const [isTruncated, setIsTruncated] = useState(true)

  // Truncate large data responses to improve browser performance
  if (isTruncated && isArray(data) && typeof data !== 'string' && size(data) > MAXLENGTH) {
    return (
      <>
        <Header title={title} copied={copied} onClick={handleCopy} />
        <JSONPretty theme={jsonPrettyTheme} data={data.slice(0, MAXLENGTH)} />
        <JSONPretty theme={jsonPrettyTheme} data={`...\n${intl.formatMessage(messages.truncatedJSONMessage)}`} />
        <Button onClick={() => setIsTruncated(false)}>{intl.formatMessage(messages.expandJSON)}</Button>
      </>
    )
  }

  // Show full response
  return (
    <>
      <Header title={title} copied={copied} onClick={handleCopy} />
      <JSONPretty theme={jsonPrettyTheme} data={data} />
    </>
  )
})
TruncatedJSONPretty.displayName = 'TruncatedJSONPretty'

export default TruncatedJSONPretty
